import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, UploadCloud, FileText, Zap, Loader2, TrendingUp, CheckCircle2, XCircle, MinusCircle, Trash2, Layers, Check, X } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT = {
  BUY: { cls: "bg-emerald-500 text-white", ring: "ring-emerald-500/30", Icon: CheckCircle2, sub: "Screens as an attractive acquisition" },
  HOLD: { cls: "bg-amber-500 text-white", ring: "ring-amber-500/30", Icon: MinusCircle, sub: "Mixed signals, dig deeper before committing" },
  PASS: { cls: "bg-rose-500 text-white", ring: "ring-rose-500/30", Icon: XCircle, sub: "Below screening thresholds" },
};

const money = (n) => (n == null ? "n/a" : "$" + Number(n).toLocaleString());
const labelCls = (l) => (l === "Strong" ? "text-emerald-500" : l === "Weak" ? "text-rose-500" : "text-amber-500");

export default function DealAnalysis() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [drag, setDrag] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [compareDeals, setCompareDeals] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [dealAudit, setDealAudit] = useState(null);

  // Live Deal Drift Check — audit the OM's market against live BLS QCEW.
  useEffect(() => {
    const mkt = result?.fields?.market;
    if (!mkt) { setDealAudit(null); return; }
    let alive = true;
    setDealAudit({ loading: true });
    axios.get(`${API}/haven/klr/deal-audit`, { params: { market: mkt } })
      .then((r) => { if (alive) setDealAudit(r.data); })
      .catch(() => { if (alive) setDealAudit({ error: true }); });
    return () => { alive = false; };
  }, [result?.analysis_id, result?.fields?.market]);

  const loadHistory = async () => {
    try { const { data } = await axios.get(`${API}/deal/history`); setHistory(data.analyses || []); } catch (_) {}
  };
  useEffect(() => { loadHistory(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setError(""); setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/deal/analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || "Analysis failed. Please try another document.");
    } finally { setLoading(false); }
  };

  const openPast = async (id) => {
    setError(""); setLoading(true);
    try { const { data } = await axios.get(`${API}/deal/history/${id}`); setResult(data); }
    catch (_) { setError("Could not load that analysis."); }
    finally { setLoading(false); }
  };
  const del = async (id, e) => {
    e.stopPropagation();
    setHistory((h) => h.filter((x) => x.analysis_id !== id));
    if (result?.analysis_id === id) setResult(null);
    try { await axios.delete(`${API}/deal/history/${id}`); } catch (_) {}
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 3 ? s : [...s, id]));
  };

  const runCompare = async () => {
    if (selectedIds.length < 2) return;
    setCompareLoading(true);
    try {
      const docs = await Promise.all(selectedIds.map((id) => axios.get(`${API}/deal/history/${id}`).then((r) => r.data)));
      setCompareDeals(docs);
    } catch (_) { setError("Could not load one of the selected deals."); }
    finally { setCompareLoading(false); }
  };

  const exportComparePdf = async () => {
    try {
      const el = document.getElementById("compare-overlay-export");
      if (!el) return;
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([import("jspdf"), import("html2canvas")]);
      const canvas = await html2canvas(el, { backgroundColor: "#0B1220", scale: 2 });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      pdf.setFillColor(11, 18, 32); pdf.rect(0, 0, pw, ph, "F");
      pdf.setTextColor(34, 211, 238); pdf.setFontSize(14); pdf.text("Deal Comparison · QNNEAL", 24, 30);
      pdf.setTextColor(120, 120, 120); pdf.setFontSize(8);
      pdf.text(`Generated ${new Date().toLocaleString()} · Linneman Associates · screening signal, not investment advice`, 24, ph - 16);
      const iw = pw - 48, ih = Math.min(ph - 80, (canvas.height / canvas.width) * iw);
      pdf.addImage(img, "PNG", 24, 46, iw, ih);
      pdf.save("deal-comparison-ic-brief.pdf");
      toast.success("PDF exported");
    } catch (_) { toast.error("Export failed"); }
  };

  const v = result ? (VERDICT[result.verdict] || VERDICT.HOLD) : null;
  const q = result?.quantum_summary || {};
  const f = result?.fields || {};

  return (
    <div className="min-h-screen bg-[#FAFBFC] dark:bg-[#070B14] text-slate-900 dark:text-slate-100" data-testid="deal-analysis-page">
      {/* Header */}
      <header className="sticky top-0 z-20 flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-white/[0.08] bg-white/90 dark:bg-[#070B14]/90 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard"))}
            data-testid="deal-back-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 dark:border-white/[0.12] text-slate-600 dark:text-slate-300 hover:border-cyan-500/60 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
            <ArrowLeft className="w-4 h-4" /> <span className="text-xs font-medium">Back</span>
          </button>
          <div>
            <div className="text-base font-bold tracking-tight">Deal Analysis</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">Offering Memorandum underwriting, benchmarking and verdict</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => { setCompareMode((m) => !m); setSelectedIds([]); }}
            data-testid="deal-compare-toggle"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${compareMode ? "border-cyan-500 text-cyan-600 dark:text-cyan-400 bg-cyan-500/10" : "border-slate-300 dark:border-white/[0.12] text-slate-600 dark:text-slate-300 hover:border-cyan-500/60"}`}>
            <Layers className="w-4 h-4" /> {compareMode ? "Exit compare" : "Compare deals"}
          </button>
          <ThemeToggle />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <div className="space-y-6">
          {/* Upload */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
            className={`rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-cyan-500 bg-cyan-500/5" : "border-slate-300 dark:border-white/[0.12]"}`}
            data-testid="deal-dropzone">
            <UploadCloud className="w-10 h-10 mx-auto text-cyan-500 mb-3" />
            <div className="text-sm font-semibold">Drop an Offering Memorandum PDF here</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">or click to browse. Max 25MB.</div>
            <button onClick={() => fileRef.current?.click()} disabled={loading} data-testid="deal-browse-btn"
              className="mt-4 px-5 py-2 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50">
              {loading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</span> : "Select PDF"}
            </button>
            <input ref={fileRef} type="file" accept=".pdf,.txt" className="hidden"
              onChange={(e) => upload(e.target.files?.[0])} data-testid="deal-file-input" />
          </div>

          {error && <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-400 px-4 py-3 text-sm" data-testid="deal-error">{error}</div>}

          {result && (
            <div className="space-y-5" data-testid="deal-result">
              {/* Verdict */}
              <div className={`rounded-2xl p-6 ring-1 ${v.ring} bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/[0.08] shadow-sm`}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">{f.property_name || result.filename} · {f.market || ""}</div>
                    <div className="mt-2 flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold ${v.cls}`} data-testid="deal-verdict">
                        <v.Icon className="w-4 h-4" /> {result.verdict}
                      </span>
                      <span className="text-sm text-slate-500 dark:text-slate-400">{v.sub}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold" data-testid="deal-score">{result.composite_score}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Composite score / 100</div>
                  </div>
                </div>
                {result.thesis && <p className="mt-4 text-[13px] leading-relaxed text-slate-600 dark:text-slate-300 border-l-2 border-cyan-500/50 pl-3">{result.thesis}</p>}
              </div>

              {/* Live Deal Drift Check — QCEW labor drift + affordability audit */}
              {dealAudit && !dealAudit.loading && (
                <div className={`rounded-2xl p-5 ring-1 border shadow-sm ${dealAudit.in_coverage && dealAudit.available ? (dealAudit.drift || dealAudit.affordability_band === "stretched" ? "ring-red-500/30 border-red-200 dark:border-red-500/20 bg-red-50/50 dark:bg-red-500/[0.04]" : "ring-emerald-500/30 border-emerald-200 dark:border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-500/[0.04]") : "ring-slate-500/10 border-slate-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.03]"}`} data-testid="deal-drift-audit">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Live labor audit · BLS QCEW</div>
                    {dealAudit.in_coverage && dealAudit.available && (
                      <div className="flex gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${dealAudit.drift ? "bg-red-500/15 text-red-600 dark:text-red-300" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"}`}>{dealAudit.drift ? "⚠ DATA DRIFT" : "✓ ANCHOR OK"}</span>
                        {dealAudit.affordability_band && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${dealAudit.affordability_band === "stretched" ? "bg-red-500/15 text-red-600 dark:text-red-300" : dealAudit.affordability_band === "headroom" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" : "bg-slate-500/15 text-slate-600 dark:text-slate-300"}`}>{dealAudit.affordability_band.toUpperCase()} · {dealAudit.rent_to_income_pct}%</span>}
                      </div>
                    )}
                  </div>
                  {!dealAudit.in_coverage ? (
                    <p className="mt-2 text-[13px] text-slate-500 dark:text-slate-400" data-testid="deal-audit-nocoverage">{dealAudit.note}</p>
                  ) : !dealAudit.available ? (
                    <p className="mt-2 text-[13px] text-slate-500 dark:text-slate-400">{dealAudit.note}</p>
                  ) : (
                    <>
                      <p className="mt-1 text-[11px] text-slate-500">Resolved to <b className="text-slate-700 dark:text-slate-200">{dealAudit.resolved_market}</b> in the KLR coverage universe</p>
                      <p className="mt-2 text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">{dealAudit.drift_detail}</p>
                      <p className="mt-1 text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">{dealAudit.affordability_signal}</p>
                      <div className="mt-2 flex flex-wrap gap-4 text-[11px] font-mono text-slate-500 dark:text-slate-400">
                        {dealAudit.avg_weekly_wage != null && <span>Avg wage: <b className="text-slate-700 dark:text-slate-200">${Number(dealAudit.avg_weekly_wage).toLocaleString()}/wk</b></span>}
                        {dealAudit.jobs_trend_pct != null && <span>Jobs {dealAudit.jobs_trend_pct >= 0 ? "+" : ""}{dealAudit.jobs_trend_pct}%</span>}
                        <span>Anchor: {dealAudit.dataset_supersector} · Live: {dealAudit.live_supersector}</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Quantum */}
              {q.variables ? (
                <div className="rounded-2xl bg-cyan-500/[0.06] border border-cyan-500/25 p-4 space-y-2" data-testid="deal-quantum">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-cyan-600 dark:text-cyan-400 font-bold"><Zap className="w-3.5 h-3.5" /> Quantum Optimization Applied</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-lg font-bold">{q.variables.toLocaleString()}</div><div className="text-[9px] uppercase tracking-wide text-slate-500">Scenario variables</div></div>
                    <div><div className="text-lg font-bold">{q.solve_time_s}s</div><div className="text-[9px] uppercase tracking-wide text-slate-500">Solve time</div></div>
                    <div><div className="text-lg font-bold">{q.optimality_gap}</div><div className="text-[9px] uppercase tracking-wide text-slate-500">Optimality gap</div></div>
                  </div>
                  <div className="text-[12px] text-slate-600 dark:text-slate-300 border-t border-slate-200 dark:border-white/[0.06] pt-2">{q.note}</div>
                </div>
              ) : null}

              {/* Factor benchmark */}
              <div className="rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/[0.08] shadow-sm overflow-hidden" data-testid="deal-factors">
                <div className="px-4 py-2.5 text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-white/[0.06]">Benchmark vs U.S. multifamily norms</div>
                <table className="w-full text-sm">
                  <thead><tr className="text-[11px] text-slate-400 uppercase tracking-wide">
                    <th className="text-left px-4 py-2 font-medium">Factor</th><th className="text-right px-4 py-2 font-medium">Value</th>
                    <th className="text-right px-4 py-2 font-medium">Benchmark</th><th className="text-right px-4 py-2 font-medium">Rating</th></tr></thead>
                  <tbody>
                    {(result.benchmark?.factors || []).map((row) => (
                      <tr key={row.key} className="border-t border-slate-100 dark:border-white/[0.05]">
                        <td className="px-4 py-2.5">{row.metric}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{row.value}{row.unit}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-slate-400">{row.higher_better ? "≥" : "≤"}{row.benchmark}{row.unit}</td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${labelCls(row.label)}`}>{row.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Extracted fields */}
              <div className="rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/[0.08] shadow-sm p-4" data-testid="deal-fields">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Extracted from the OM</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  {[["Units", f.units], ["Year built", f.year_built], ["Asking price", money(f.asking_price_usd)],
                    ["NOI", money(f.noi_usd)], ["Avg rent", money(f.avg_monthly_rent_usd)], ["Cap rate", f.cap_rate_pct != null ? f.cap_rate_pct + "%" : "n/a"]].map(([k, val]) => (
                    <div key={k}><div className="text-[10px] uppercase tracking-wide text-slate-400">{k}</div><div className="font-semibold">{val ?? "n/a"}</div></div>
                  ))}
                </div>
              </div>
              <div className="text-[11px] text-slate-400">{result.disclaimer}</div>
            </div>
          )}
        </div>

        {/* History */}
        <aside className="space-y-2" data-testid="deal-history">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Recent analyses</div>
          {compareMode && (
            <button onClick={runCompare} disabled={selectedIds.length < 2 || compareLoading}
              data-testid="deal-run-compare-btn"
              className="w-full rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-xs font-semibold px-3 py-2">
              {compareLoading ? "Loading…" : `Compare selected (${selectedIds.length})`}
            </button>
          )}
          {history.length === 0 && <div className="text-xs text-slate-400">No analyses yet.</div>}
          {history.map((h) => {
            const sel = selectedIds.includes(h.analysis_id);
            return (
            <div key={h.analysis_id}
              onClick={(e) => (compareMode ? toggleSelect(h.analysis_id, e) : openPast(h.analysis_id))}
              data-testid={`deal-history-${h.analysis_id}`}
              className={`w-full cursor-pointer text-left rounded-xl border bg-white dark:bg-white/[0.03] px-3 py-2.5 transition-colors group ${sel ? "border-cyan-500 ring-1 ring-cyan-500/40" : "border-slate-200 dark:border-white/[0.08] hover:border-cyan-500/50"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {compareMode && (
                    <span className={`flex h-4 w-4 items-center justify-center rounded border ${sel ? "bg-cyan-500 border-cyan-500" : "border-slate-400"}`}>
                      {sel && <Check className="w-3 h-3 text-white" />}
                    </span>
                  )}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${(VERDICT[h.verdict] || VERDICT.HOLD).cls}`}>{h.verdict}</span>
                </div>
                {!compareMode && <Trash2 onClick={(e) => del(h.analysis_id, e)} data-testid={`deal-history-delete-${h.analysis_id}`}
                  className="w-3.5 h-3.5 text-slate-400 opacity-60 group-hover:opacity-100 hover:text-rose-500 cursor-pointer" />}
              </div>
              <div className="mt-1 text-xs font-medium truncate">{h.filename}</div>
              <div className="text-[10px] text-slate-400">Score {h.composite_score}</div>
            </div>
            );
          })}
        </aside>
      </div>

      {/* Compare deals overlay — side-by-side verdicts + factor matrix */}
      {compareDeals && compareDeals.length >= 2 && (() => {
        const rowKeys = [];
        compareDeals.forEach((d) => (d.benchmark?.factors || []).forEach((f) => { if (!rowKeys.includes(f.key)) rowKeys.push(f.key); }));
        const cell = (d, key) => (d.benchmark?.factors || []).find((f) => f.key === key);
        const winners = {};
        rowKeys.forEach((key) => {
          let best = -1, idx = -1;
          compareDeals.forEach((d, i) => { const c = cell(d, key); if (c && c.score > best) { best = c.score; idx = i; } });
          winners[key] = idx;
        });
        const bestScoreIdx = compareDeals.reduce((bi, d, i, arr) => (d.composite_score > arr[bi].composite_score ? i : bi), 0);
        const leader = compareDeals[bestScoreIdx];
        const leaderWinKeys = rowKeys.filter((k) => winners[k] === bestScoreIdx);
        const leaderWinMetrics = leaderWinKeys.map((k) => cell(compareDeals.find((d) => cell(d, k)), k)?.metric).filter(Boolean);
        const verdictLine = `${leader.fields?.property_name || leader.filename} leads with a ${leader.verdict} (composite ${leader.composite_score}) — strongest on ${leaderWinMetrics.slice(0, 3).join(", ") || "overall fundamentals"}${leaderWinMetrics.length > 3 ? ` +${leaderWinMetrics.length - 3} more` : ""}.`;
        return (
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/70 p-4 sm:p-8 backdrop-blur-sm" onClick={() => setCompareDeals(null)} data-testid="compare-overlay">
            <div id="compare-overlay-export" onClick={(e) => e.stopPropagation()} className="w-full max-w-6xl rounded-2xl border border-slate-200 dark:border-white/[0.1] bg-white dark:bg-[#0B1220] p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold flex items-center gap-2"><Layers className="w-5 h-5 text-cyan-500" /> Deal comparison · {compareDeals.length} OMs</h2>
                <div className="flex items-center gap-2">
                  <button onClick={exportComparePdf} data-testid="compare-export-pdf" className="rounded-md border border-cyan-500/50 px-3 py-1.5 text-xs font-medium text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/10">↓ Export IC PDF</button>
                  <button onClick={() => setCompareDeals(null)} data-testid="compare-close-btn" className="rounded-md p-1.5 text-slate-400 hover:text-slate-800 dark:hover:text-white"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="mt-3 rounded-xl border border-cyan-500/30 bg-cyan-500/[0.06] px-4 py-3 text-sm text-slate-700 dark:text-cyan-100" data-testid="compare-verdict-line">
                <span className="font-semibold text-cyan-600 dark:text-cyan-300">IC read-out:</span> {verdictLine}
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wide text-slate-400 font-medium">Metric</th>
                      {compareDeals.map((d, i) => (
                        <th key={i} className={`px-3 py-2 text-center align-top ${i === bestScoreIdx ? "bg-cyan-500/[0.06]" : ""}`}>
                          <div className="text-xs font-semibold truncate max-w-[180px]">{d.fields?.property_name || d.filename}</div>
                          <div className="text-[10px] text-slate-400">{d.fields?.market || ""}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-slate-100 dark:border-white/[0.06]">
                      <td className="px-3 py-2.5 text-[11px] uppercase tracking-wide text-slate-400">Verdict</td>
                      {compareDeals.map((d, i) => (
                        <td key={i} className={`px-3 py-2.5 text-center ${i === bestScoreIdx ? "bg-cyan-500/[0.06]" : ""}`}>
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold ${(VERDICT[d.verdict] || VERDICT.HOLD).cls}`} data-testid={`compare-verdict-${i}`}>{d.verdict}</span>
                        </td>
                      ))}
                    </tr>
                    <tr className="border-t border-slate-100 dark:border-white/[0.06]">
                      <td className="px-3 py-2.5 text-[11px] uppercase tracking-wide text-slate-400">Composite score</td>
                      {compareDeals.map((d, i) => (
                        <td key={i} className={`px-3 py-2.5 text-center font-bold text-lg ${i === bestScoreIdx ? "bg-cyan-500/[0.06] text-cyan-600 dark:text-cyan-400" : ""}`}>{d.composite_score}{i === bestScoreIdx && <span className="ml-1 text-[10px] align-top text-cyan-500">★</span>}</td>
                      ))}
                    </tr>
                    {rowKeys.map((key) => (
                      <tr key={key} className="border-t border-slate-100 dark:border-white/[0.06]">
                        <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{cell(compareDeals.find((d) => cell(d, key)), key)?.metric}</td>
                        {compareDeals.map((d, i) => {
                          const c = cell(d, key);
                          const win = winners[key] === i;
                          return (
                            <td key={i} className={`px-3 py-2.5 text-center font-mono ${win ? "bg-emerald-500/[0.08]" : ""}`}>
                              {c ? <><span className={win ? "font-bold text-emerald-600 dark:text-emerald-400" : ""}>{c.value}{c.unit}</span> <span className={`ml-1 text-[10px] ${labelCls(c.label)}`}>{c.label}</span></> : <span className="text-slate-400">n/a</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {[["Units", "units"], ["Asking price", "asking_price_usd"], ["NOI", "noi_usd"], ["Avg rent", "avg_monthly_rent_usd"]].map(([lbl, fk]) => (
                      <tr key={fk} className="border-t border-slate-100 dark:border-white/[0.06]">
                        <td className="px-3 py-2.5 text-[11px] uppercase tracking-wide text-slate-400">{lbl}</td>
                        {compareDeals.map((d, i) => {
                          const val = d.fields?.[fk];
                          return <td key={i} className="px-3 py-2.5 text-center font-mono text-slate-600 dark:text-slate-300">{val == null ? "n/a" : (fk.includes("usd") ? money(val) : Number(val).toLocaleString())}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-[11px] text-slate-400">Best composite marked ★; per-factor leader highlighted green. Screening signals benchmarked against U.S. multifamily norms, not investment advice.</p>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
